import { motion, useTransform, useMotionValue, animate, MotionValue } from "framer-motion";
import { MapPin, Star, CheckCircle, Building2 } from "lucide-react";
import { MOCK_BUSINESS } from "./mockData";
import { useEffect, useState } from "react";

interface Step1GoogleMapsProps {
  scrollProgress: MotionValue<number>;
}

export const Step1GoogleMaps = ({ scrollProgress }: Step1GoogleMapsProps) => {
  // Map scroll progress to reveal different elements
  // 0-0.2: Map and pin appear
  // 0.2-0.4: Business name, address appear
  // 0.4-0.6: Phone, website appear
  // 0.6-0.8: Business types appear
  // 0.8-1.0: Rating and hours appear

  // Animation ranges (parent handles initial pause before scrollProgress starts moving)
  const mapOpacity = useTransform(scrollProgress, [0, 0.15], [0, 1]);
  const pinOpacity = useTransform(scrollProgress, [0.05, 0.2], [0, 1]);
  const cardOpacity = useTransform(scrollProgress, [0.05, 0.2], [0, 1]);

  // Typing animation for search bar - tied to scroll
  const searchText = "Café Bratislava";
  const [displayedChars, setDisplayedChars] = useState(0);

  useEffect(() => {
    const unsubscribe = scrollProgress.on("change", (value) => {
      // Type AFTER map is visible (starts at 0.18, ends at 0.50 for longer animation)
      const TYPING_START = 0.18;
      const TYPING_END = 0.50;
      const progress = Math.max(0, Math.min(1, (value - TYPING_START) / (TYPING_END - TYPING_START)));
      const chars = Math.floor(progress * searchText.length);
      setDisplayedChars(chars);
    });
    return unsubscribe;
  }, [scrollProgress]);

  // Sequence: typing (0.18-0.50) → success message (0.52-0.60) → data reveal (0.62+)
  const successOpacity = useTransform(scrollProgress, [0.52, 0.60], [0, 1]);
  const nameOpacity = useTransform(scrollProgress, [0.62, 0.70], [0, 1]);
  const addressOpacity = useTransform(scrollProgress, [0.68, 0.76], [0, 1]);
  const phoneOpacity = useTransform(scrollProgress, [0.74, 0.82], [0, 1]);
  const websiteOpacity = useTransform(scrollProgress, [0.78, 0.86], [0, 1]);
  const typesOpacity = useTransform(scrollProgress, [0.82, 0.90], [0, 1]);
  const ratingOpacity = useTransform(scrollProgress, [0.86, 0.94], [0, 1]);
  const hoursOpacity = useTransform(scrollProgress, [0.90, 0.98], [0, 1]);

  return (
    <div className="max-w-7xl mx-auto w-full h-full flex flex-col overflow-hidden py-6">
      {/* Main Content */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        {/* Map Preview */}
        <motion.div
          style={{ opacity: mapOpacity }}
          className="relative h-full min-h-[180px] rounded-xl overflow-hidden border border-border bg-card/50"
        >
          {/* Map Background - Google Maps Embed */}
          <div className="absolute inset-0">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d20000!2d17.1077!3d48.1486!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2ssk!4v1"
              className="w-full h-full border-0 pointer-events-none"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Map of Bratislava"
            />
          </div>

          {/* Animated Map Pin */}
          <motion.div
            style={{ opacity: pinOpacity }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-full"
          >
            <div className="relative">
              {/* Pin shadow/pulse effect */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.2, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 bg-emerald-500/30 rounded-full blur-xl"
              />
              {/* Actual pin */}
              <div className="relative bg-emerald-500 p-4 rounded-full shadow-2xl">
                <MapPin className="w-8 h-8 text-white" />
              </div>
            </div>
          </motion.div>

          {/* Map Label */}
          <div className="absolute bottom-2 left-2 right-2 bg-background/90 backdrop-blur-sm border border-border rounded-lg p-3">
            <p className="text-sm text-muted-foreground">
              Search: <span className="text-foreground">{searchText.slice(0, displayedChars)}</span>
              {displayedChars < searchText.length && (
                <span className="inline-block w-[2px] h-4 bg-foreground ml-0.5 animate-pulse" />
              )}
            </p>
          </div>

          {/* Success Message */}
          <motion.div
            style={{ opacity: successOpacity }}
            className="absolute top-2 left-2 right-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                Business found!
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Business Data Card */}
        <motion.div
          style={{ opacity: cardOpacity }}
          className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4 overflow-hidden h-full flex flex-col"
        >
          <div className="h-full flex flex-col min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between shrink-0 mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <MapPin className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-0.5">
                    Business Information
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Auto-fetched from Google Maps
                  </p>
                </div>
              </div>
              <motion.div style={{ opacity: successOpacity }}>
                <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
              </motion.div>
            </div>

            {/* Data Fields - Flex column with even distribution */}
            <div className="flex-1 min-h-0 flex flex-col justify-between">
              {/* Section 1: Name + Address */}
              <div className="border-b border-border pb-3">
                <motion.div style={{ opacity: nameOpacity }} className="mb-2">
                  <p className="text-xs text-muted-foreground">Business Name</p>
                  <p className="text-sm text-foreground font-medium">{MOCK_BUSINESS.name}</p>
                </motion.div>
                <motion.div style={{ opacity: addressOpacity }}>
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm text-foreground">{MOCK_BUSINESS.address}</p>
                </motion.div>
              </div>

              {/* Section 2: Phone + Website */}
              <div className="grid grid-cols-2 gap-3 border-b border-border py-3">
                <motion.div style={{ opacity: phoneOpacity }}>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="text-sm text-foreground">{MOCK_BUSINESS.phone}</p>
                </motion.div>
                <motion.div style={{ opacity: websiteOpacity }}>
                  <p className="text-xs text-muted-foreground">Website</p>
                  <p className="text-sm text-foreground truncate">{MOCK_BUSINESS.website}</p>
                </motion.div>
              </div>

              {/* Section 3: Business Type Tags */}
              <motion.div
                style={{ opacity: typesOpacity }}
                className="py-3"
              >
                <p className="text-xs text-muted-foreground mb-2">Business Type</p>
                <div className="flex flex-wrap gap-2">
                  {MOCK_BUSINESS.businessTypes.map((type) => (
                    <span
                      key={type}
                      className="px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs border border-indigo-500/20"
                    >
                      <Building2 className="w-3.5 h-3.5 inline mr-1" />
                      {type}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Section 4: Rating + Hours */}
              <div className="grid grid-cols-2 gap-3 pt-3">
                <motion.div style={{ opacity: ratingOpacity }}>
                  <p className="text-xs text-muted-foreground mb-1">Current Rating</p>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= Math.floor(MOCK_BUSINESS.rating)
                              ? "fill-amber-400 text-amber-400"
                              : "text-muted-foreground/30"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-foreground font-medium">
                      {MOCK_BUSINESS.rating} ({MOCK_BUSINESS.totalReviews})
                    </span>
                  </div>
                </motion.div>
                <motion.div style={{ opacity: hoursOpacity }}>
                  <p className="text-xs text-muted-foreground mb-1">Opening Hours</p>
                  <p className="text-sm text-foreground">
                    {MOCK_BUSINESS.openingHours}
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
